<template>
	<header :class="['bg-color-content-primary text-color-white pt-100']">
		<div
			class="container display-flex align-items-center align-items-stretch-lg justify-content-space-between-md flex-wrap-wrap pt-300 pb-100 py-400-md"
		>
			<div v-reveal:fade-up :class="['text-align-center-smo col-12 col-6-md']">
				<div class="display-flex justify-content-center-smo">
					<img :src="logoImage" alt="Fundrise Income Fund" style="width: 170px" />
					<h1 class="visually-hidden">Fundrise Income Fund</h1>
				</div>

				<p class="heading-1 mt-200">The Income Fund offers a 7.5% current distribution rate</p>
				<p class="body-lg mt-50 mt-100-smo mb-200 col-11-lg"
					>The Fundrise Income Fund aims to deliver consistent cash flow, even if interest rates start to
					fall. The Fund offers access to a diversified portfolio of cash flowing assets, all professionally
					managed by our expert team.</p
				>
				<ul class="icon-data-list display-flex flex-direction-column gap-100 mb-200">
					<li v-for="data in iconDataList" :key="data.icon" class="display-flex gap-100">
						<BaseSvgIcon :name="data.icon" width="24" height="24" />
						<span class="body-md" v-html="data.label"></span>
					</li>
				</ul>
				<BasePlaceHolder
					:class="[
						'display-flex flex-direction-column',
						{ 'flex-direction-column-reverse gap-100': thirdPartyAuthStore.isSignupEnabled }
					]"
					:section-count="1"
					:line-count="2"
					:placehold="loading"
					theme="dark"
				>
					<MarketingHeroCta
						class="text-align-left"
						:form-config="presetPlanFundedLeadCaptureConfig"
						:offering-id="offeringId"
						:is-dark-mode="true"
					/>

					<p class="body-sm text-color-gray-20"
						>Start investing in less than 5 minutes and with as little as $10.</p
					>
				</BasePlaceHolder>
			</div>
			<figure
				v-if="!loading"
				v-reveal:fade-up
				:class="['mx-0 my-0 col-12 col-5-md mt-200-smo display-flex align-items-center position-relative']"
			>
				<div class="hero-image-mobile-gradient"></div>
				<img data-test="hero-image" :src="imgUrl" alt="" class="hero-image-container" />
			</figure>
		</div>
		<div class="container display-flex justify-content-center">
			<div class="col-9-md text-align-center py-100 py-200-md body-xs muted">
				<p>
					Performance as of 12/31/2024 and may be rounded. Current distribution is calculated by annualizing
					current month's distribution and dividing by the prior month's net asset value per share.
					Distributions may fluctuate, distributions are not guaranteed, and past performance is not
					indicative of future results.</p
				>
			</div>
		</div>
	</header>
</template>

<script setup lang="ts">
import { appCloudfrontPath } from '@constants';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import MarketingHeroCta from '@components/marketing-site/marketing-hero-cta.vue';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';

interface Props {
	loading: boolean;
	offeringId: string;
	presetPlanFundedLeadCaptureConfig: LeadCaptureFormConfig;
}

defineProps<Props>();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const logoImage = `${appCloudfrontPath}/images/global/fundrise-income-fund-logo-white.svg`;
const imgUrl = `${appCloudfrontPath}/images/marketing-site/funds/income-fund-hero.png`;

const iconDataList = [
	{
		icon: 'line-chart',
		label: '8.3% trailing 12-month cumulative returns<sup>1</sup>'
	},
	{
		icon: 'direct-invest',
		label: 'Quarterly, penalty-free withdrawals'
	},
	{
		icon: 'performance-outline',
		label: 'Flexible minimums starting at just $10'
	}
];
</script>

<style lang="scss" scoped>
@use '../../../styles/utilities/respond-to.scss' as *;

.hero-image-mobile-gradient {
	@include respond-to(smo) {
		background: linear-gradient(180deg, rgba(21, 21, 19, 0) 60%, rgba(21, 21, 19, 1) 95%);
		height: 100%;
		width: 100%;
		position: absolute;
		max-height: 300px;
	}
}

.hero-image-container {
	@include respond-to(md) {
		width: 100%;
		object-fit: contain;
		position: relative;
		right: -5rem;
	}

	@include respond-to(smo) {
		max-height: 300px;
		max-width: 375px;
		object-fit: cover;
		object-position: top center;
		margin-inline: auto;
	}
}

.icon-data-list {
	@include respond-to(smo) {
		width: fit-content;
		margin-inline: auto;
	}
}
</style>
